import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { useToyotaMap } from "../../../common-deprecated/ToyotaMap";
import { useUscSelector } from "../redux/store";

type UseDeliveryConfiguratorMap = {
    mapInitialized: boolean;
};

// This hook is very similar to useLocationMap, however it makes more sense to split them up since
// there's still enough differences where it's not worth trying to combine them.
const useDeliveryConfiguratorMap = (mapContainer: RefObject<HTMLDivElement>): UseDeliveryConfiguratorMap => {
    const { mapboxToken, country, brand } = useUscSelector((state) => state.commonSettings);
    const selectedDealer = useUscSelector(
        (state) => state.carDelivery.tempDeliveryConfig || state.carDelivery.deliveryConfig,
    );
    const mapRef = useRef<any>(null);
    const [mapInitialized, setMapInitialized] = useState<boolean>(false);

    // TODO: No Lexus marker support yet in the mapbox wrappers so we use a generic blue marker for Lexus. See OR-7398
    // This might not be relevant for Car Delivery since there's no Lexus support (yet), but I'm leaving it in for now.
    const markerImage = brand === "toyota" ? "toyota-red-logo" : "toyota-blue";

    const selectedLocation = selectedDealer?.address?.geo;

    const mapWrapper = useToyotaMap();

    useEffect(() => {
        if (mapWrapper && mapContainer.current) {
            const initMap = async (): Promise<void> => {
                mapRef.current = new mapWrapper.DynamicMap(mapContainer.current, {
                    accessToken: mapboxToken,
                    boundsPaddingTop: 100,
                });

                // Uncomment code below to easily test mapbox transitions in console.
                // window.testMap = mapRef.current;

                // Set initialized flag true which should hide the preloader and trigger effects below.
                setMapInitialized(true);
            };
            initMap();
        }
    }, [mapContainer, mapWrapper, mapboxToken]);

    // ----------------------------------------------------------------------
    // Map transitions
    // ----------------------------------------------------------------------
    useEffect(() => {
        if (mapInitialized) {
            mapRef.current.clearMarkers();

            if (selectedLocation) {
                mapRef.current.focusOn(selectedLocation);

                mapRef.current.addMarker(selectedLocation, { markerImage });
            } else {
                // No location selected, so just focus on the country.
                mapRef.current.focusOn(country);
            }
        }
    }, [country, mapInitialized, markerImage, selectedLocation]);

    return {
        mapInitialized,
    };
};

export default useDeliveryConfiguratorMap;
